import React, { Component } from 'react';
import TorGrid from './../torgrid/torgrid';
import ProjectionCashflowChart from "./components/projectionCashflowChart.js";
import { torGridBALService } from '../../bal/torGridBALService.bal';

class PortfolioProjection extends Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            gridData: [],
            filterData: [],
            loadError: null
        }
        this.dataRequestId = 0;

        this.getData = this.getData.bind(this);
    }

    render() {
        return this.getTemplate();
    }

    getFilteredData(data, colName, value) {
        const rows = Array.isArray(data) ? data : [];
        return !value || value === 'All'
            ? rows
            : rows.filter(item => item[colName] === value);
    }

    normalizeCashflowAmount(amount) {
        if (typeof amount === 'number') {
            return Number.isFinite(amount) ? amount : 0;
        }

        if (typeof amount === 'string') {
            const normalizedAmount = amount.replace(/,/g, '').trim();
            const parsedAmount = Number(normalizedAmount);
            return Number.isFinite(parsedAmount) ? parsedAmount : 0;
        }

        return 0;
    }

    normalizeCashflowRows(data) {
        return data.map(row => ({
            ...row,
            'Cashflow Book Amount': this.normalizeCashflowAmount(row?.['Cashflow Book Amount'])
        }));
    }

    hasNonZeroCashflow(data) {
        return data.some(row => this.normalizeCashflowAmount(row?.['Cashflow Book Amount']) !== 0);
    }

    filterData(colName, value) {
        this.setState(prevState => ({
            filterData: this.getFilteredData(prevState.gridData, colName, value)
        }));
    }

    getData = async () => {
        const { projectionDeal } = this.props;
        const requestId = ++this.dataRequestId;

        try {
            const data = await torGridBALService.getRowData("pCashflow", { query: `Exec [sp_projected_cashflow] '${projectionDeal}'` });
            if (requestId !== this.dataRequestId) {
                return;
            }

            const gridData = this.normalizeCashflowRows(Array.isArray(data) ? data : []);
            this.setState({
                gridData,
                filterData: this.getFilteredData(gridData, 'Fund', this.props.projectionFund),
                isLoading: false,
                loadError: null
            });
        } catch (error) {
            if (requestId === this.dataRequestId) {
                this.setState({
                    isLoading: false,
                    gridData: [],
                    filterData: [],
                    loadError: error?.message || 'Unable to load projected cashflows.'
                });
            }
        }
    }

    componentDidMount() {
        if (this.props.projectionDeal) {
            this.setState({ isLoading: true, gridData: [], filterData: [], loadError: null });
            this.getData();
        }
    }

    componentDidUpdate(prevProps) {
        const hasDealChanged = (prevProps.projectionDeal ?? '') !== (this.props.projectionDeal ?? '');

        if (hasDealChanged) {
            if (this.props.projectionDeal) {
                this.setState({ isLoading: true, gridData: [], filterData: [], loadError: null });
                this.getData();
            } else {
                this.dataRequestId += 1;
                this.setState({ isLoading: false, gridData: [], filterData: [], loadError: null });
            }
        }

        if (!hasDealChanged && prevProps.projectionFund !== this.props.projectionFund) {
            this.filterData('Fund', this.props.projectionFund);
        }
    }

    componentWillUnmount() {
        this.dataRequestId += 1;
    }

    getTemplate() {
        const { filterData, isLoading, loadError } = this.state;
        const hasNonZeroCashflow = this.hasNonZeroCashflow(filterData);
        if (this.props.Fund) {
            return (
                <div style={this.props.isCollapsed ? { gridTemplateColumns: 'minmax(0, 1fr)', display: 'grid', height: '100%', minHeight: 0, width: '100%' } : { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', display: 'grid', height: '100%', minHeight: 0, width: '100%' }}>
                    <div style={{ height: '100%', width: '100%', minHeight: 0, minWidth: 0 }}>
                        <TorGrid
                            setAgGridAPI={this.props.setAgGridAPI}
                            guikey="portfolioProjection"
                            rowData={this.props.gridData}
                            isHideDateInputs={true}
                            gridIdentityColumn="projectionId"
                            groupBy="Fund"
                            className="ag-theme-alpine"
                            groupDefaultExpanded={-1}
                            rowSelection={'multiple'}
                            isApprover = {this.props.isCollapsed}
                            approve = {this.props.approve}
                            isPanelCollapsed = {this.props.isCollapsed}
                            togglePanel = {this.props.togglePanelCollapse}
                            // isRowSelectable={(node) => { return true && (node.data ? node.data['Approval Status'] !== "Approved" : false) }}
                            rowSelected={this.props.rowSelected}
                            groupSelectsChildren={true}
                        />
                    </div>
                    {
                        !this.props.isCollapsed &&
                        <div style={{ height: '100%', width: '100%', minHeight: 0, minWidth: 0 }}>
                            <div style={{ gridTemplateRows: 'auto minmax(320px, 40vh) minmax(0, 1fr)', height: '100%', minHeight: 0, width: '100%', display: 'grid' }}>
                                <div>
                                    <div style={{ float: "right", marginTop: "5px" }}>
                                        <button key='approveButton' className="blueB-no-margin" onClick={this.props.approve}>Approve</button>
                                    </div>
                                    <div style={{ float: "right", marginTop: "5px" }}>
                                        <button key='toggleChartsButton' className="blueB-no-margin" onClick={this.props.togglePanelCollapse}>Hide Charts</button>
                                    </div>
                                </div>
                                <div style={{ height: '100%', minHeight: '320px', width: '100%', position: 'relative' }}>
                                    {isLoading
                                        ? <div style={{ padding: '15px' }}>Loading projected cashflows...</div>
                                        : loadError
                                            ? <div style={{ padding: '15px', color: '#a94442' }}>{loadError}</div>
                                            : !this.props.projectionDeal
                                                ? <div style={{ padding: '15px' }}>Select a projection row to display its chart.</div>
                                                : filterData.length === 0
                                                    ? <div style={{ padding: '15px' }}>No projected cashflows are available for the selected deal and fund.</div>
                                                    : !hasNonZeroCashflow
                                                        ? <div style={{ padding: '15px' }}>Projected cashflows are all zero for the selected deal and fund.</div>
                                                    : <ProjectionCashflowChart
                                                        key={`${this.props.projectionDeal ?? ''}-${this.props.projectionFund ?? ''}`}
                                                        chartTitle={`${this.props.projectionDeal} Projected CF (USD)`}
                                                        dataList={filterData}
                                                    />}
                                </div>
                                <div style={{ height: '100%', minHeight: 0, width: '100%' }}>
                                    <TorGrid
                                        guikey="pCashflow"
                                        isHideDateInputs={true}
                                        isHideSaveGridLayout={true}
                                        isShowGrid={!isLoading}
                                        isShowLoad={isLoading}

                                        rowData={filterData}
                                        gridIdentityColumn="position_id"
                                        groupIncludeFooter={true}
                                        groupIncludeTotalFooter={true}
                                        suppressExpandablePivotGroups={true}
                                        className="ag-theme-alpine"
                                        enableFilter={false}
                                        groupDefaultExpanded={-1}

                                        defaultColDef={this.props.defaultColDef ??
                                        {
                                            sortable: false
                                            , filter: false
                                            , floatingFilter: false
                                            , enableRowGroup: true
                                            , resizable: true
                                            , suppressSizeToFit: false
                                        }
                                        }

                                        handleRowSelected={this.handleRowSelected}
                                    />
                                </div>
                            </div>
                        </div>
                    }

                </div>
            );
        } else {
            return null;
        }
    }
}
export default PortfolioProjection;